import React, { useState, useMemo } from 'react';
import {
  ChartBarIcon,
  CalculatorIcon,
  BuildingOfficeIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarIcon,
  MapPinIcon,
  TagIcon,
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import { ratioData, RatioData } from './data/projectsData';

import { CATEGORIES_LOTS } from './data/lotsData';
import { computeAllLotsStats } from './data/ratioStats';
import ProjectSearchAndFilters from './Ratio_ProjectSearchAndFilters';
import RatioRangeBar from './Ratio_RangeBar';
import MaitriseBudget from './Ratio_MaitriseBudget';
import AuditLot from './Ratio_AuditLot';
import RatioUnitSelector from './Ratio_UnitSelector';
import RatioFacadePrototype from './Ratio_FacadePrototype';
import RatioGrosOeuvrePrototype from './Ratio_GrosOeuvrePrototype';
import RatioGrosOeuvreDirection from './Ratio_GrosOeuvreDirection';
import { useFilters } from '../99-contexts/FilterContext';
import { useRatioPromotion } from '../99-contexts/RatioPromotionContext';
import { ArrowRightCircleIcon } from '@heroicons/react/24/solid';

type UnitMeta = {
  key: string;
  label: string;
  shortLabel: string;
  ratioSuffix: string;
  inputLabel: string;
  totalLabel: string;
  inputUnit: string;
};

const AVAILABLE_UNITS: UnitMeta[] = [
  { key: 'm2-sdp',      label: 'm² SDP',        shortLabel: 'm²',     ratioSuffix: '€/m²',       inputLabel: 'Surface à estimer',       totalLabel: 'Surface totale',          inputUnit: 'm²' },
  { key: 'logements',   label: 'Logements',     shortLabel: 'logts',  ratioSuffix: '€/logement', inputLabel: 'Nombre de logements',     totalLabel: 'Logements totaux',        inputUnit: 'logts' },
  { key: 'parkings',    label: 'Places parking', shortLabel: 'places', ratioSuffix: '€/place',    inputLabel: 'Nombre de places',        totalLabel: 'Places totales',          inputUnit: 'places' },
  { key: 'm3-beton',    label: 'm³ béton',      shortLabel: 'm³',     ratioSuffix: '€/m³',       inputLabel: 'Volume béton à estimer',  totalLabel: 'Volume béton total',      inputUnit: 'm³' },
  { key: 'lits',        label: 'Lits',          shortLabel: 'lits',   ratioSuffix: '€/lit',      inputLabel: 'Nombre de lits',          totalLabel: 'Lits totaux',             inputUnit: 'lits' },
  { key: 'm2-plancher', label: 'm² plancher',   shortLabel: 'm² pl.', ratioSuffix: '€/m²',       inputLabel: 'Surface plancher',        totalLabel: 'Surface plancher totale', inputUnit: 'm²' },
  { key: 'chambres',    label: 'Chambres',      shortLabel: 'ch.',    ratioSuffix: '€/chambre',  inputLabel: 'Nombre de chambres',      totalLabel: 'Chambres totales',        inputUnit: 'ch.' },
];

function getUnitQuantity(project: RatioData, unitKey: string): number {
  switch (unitKey) {
    case 'm2-sdp':      return project.surface;
    case 'logements':   return project.nbLogements ?? 0;
    case 'parkings':    return project.nbParkings ?? 0;
    case 'm3-beton':    return project.m3Beton ?? 0;
    case 'lits':        return project.nbLits ?? 0;
    case 'm2-plancher': return project.m2Plancher ?? 0;
    case 'chambres':    return project.nbChambres ?? 0;
    default:            return 0;
  }
}



interface RatioProps {
  onNavigateToChiffrage?: () => void;
}

const Ratio: React.FC<RatioProps> = ({ onNavigateToChiffrage }) => {
  // Use the filter context
  const { filters: contextFilters } = useFilters();
  const { setPendingPromotion } = useRatioPromotion();



  // UI state
  const [activeUnitKey, setActiveUnitKey] = useState<string>('m2-sdp');
  const activeUnit = useMemo(
    () => AVAILABLE_UNITS.find(u => u.key === activeUnitKey) ?? AVAILABLE_UNITS[0],
    [activeUnitKey],
  );
  const [activeTab, setActiveTab] = useState<'ratioOperation' | 'ratioLot' | 'maitriseBudget' | 'auditLot' | 'facadeProto' | 'goProto' | 'goDirection'>('ratioOperation');
  const [expandedProjets, setExpandedProjets] = useState<Set<string>>(new Set());
  const [expandedLotCategories, setExpandedLotCategories] = useState<Set<string>>(new Set());
  const [expandedLots, setExpandedLots] = useState<Set<string>>(new Set());
  const [surfaceInput, setSurfaceInput] = useState<string>('1000');
  const [showCalculator, setShowCalculator] = useState<boolean>(false);
  const [calculatorView, setCalculatorView] = useState<'families' | 'lots'>('families');

  // Projects matching all context filters (except the unit selector)
  const projectsMatchingFilters = useMemo(() => {
    return ratioData.filter(projet => {
      if (contextFilters.searchTerm && !projet.projet.toLowerCase().includes(contextFilters.searchTerm.toLowerCase())) {
        return false;
      }
      if (contextFilters.typologie && projet.parameters.TypologieProgramme !== contextFilters.typologie) return false;
      if (contextFilters.projet && !projet.projet.toLowerCase().includes(contextFilters.projet.toLowerCase())) return false;
      if (contextFilters.modalite && projet.parameters.ModaliteProgramme !== contextFilters.modalite) return false;
      if (contextFilters.devolution && projet.parameters.DevolutonMarche !== contextFilters.devolution) return false;
      if (contextFilters.gamme && projet.parameters.Gamme !== contextFilters.gamme) return false;
      if (contextFilters.annee && projet.dateCreation !== contextFilters.annee) return false;
      if (contextFilters.surfaceMin) {
        const minSurface = parseFloat(contextFilters.surfaceMin);
        if (projet.surface < minSurface) return false;
      }
      if (contextFilters.surfaceMax) {
        const maxSurface = parseFloat(contextFilters.surfaceMax);
        if (projet.surface > maxSurface) return false;
      }
      return true;
    });
  }, [contextFilters]);

  // Per-unit counts (used by the selector pills)
  const unitCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const u of AVAILABLE_UNITS) {
      counts[u.key] = projectsMatchingFilters.filter(p => p.unitesDisponibles.includes(u.key)).length;
    }
    return counts;
  }, [projectsMatchingFilters]);

  // Projects actually displayed: filter by active unit availability
  const filteredProjets = useMemo(
    () => projectsMatchingFilters.filter(p => p.unitesDisponibles.includes(activeUnitKey)),
    [projectsMatchingFilters, activeUnitKey],
  );

  // Conversion factor m² per unit (average across filtered projects) — converts €/m² stats to €/unit
  const unitConversionFactor = useMemo(() => {
    if (activeUnitKey === 'm2-sdp') return 1;
    let totalSurface = 0;
    let totalUnit = 0;
    for (const p of filteredProjets) {
      totalSurface += p.surface;
      totalUnit += getUnitQuantity(p, activeUnitKey);
    }
    return totalUnit > 0 ? totalSurface / totalUnit : 1;
  }, [filteredProjets, activeUnitKey]);

  // Statistiques de distribution par lot pour le calculateur
  const lotsStats = useMemo(() => computeAllLotsStats(filteredProjets), [filteredProjets]);

  // Fourchette budgétaire totale (min/médiane/max) pour la quantité saisie dans l'unité active
  const totalEstimatedCostRange = useMemo(() => {
    const userInput = parseFloat(surfaceInput) || 0;
    if (userInput <= 0) return { min: 0, median: 0, max: 0 };
    const equivalentM2 = userInput * unitConversionFactor;
    let min = 0, median = 0, max = 0;
    lotsStats.forEach(dist => {
      if (dist.nbProjets >= 2) {
        min += dist.min * equivalentM2;
        median += dist.median * equivalentM2;
        max += dist.max * equivalentM2;
      }
    });
    return { min: Math.round(min), median: Math.round(median), max: Math.round(max) };
  }, [lotsStats, surfaceInput, unitConversionFactor]);

  // Calculate lot ratios based on filtered projects
  const lotRatios = useMemo(() => {
    const lotData: { [lotId: string]: any } = {};
    
    filteredProjets.forEach(projet => {
      projet.lots.forEach(lot => {
        if (!lotData[lot.id]) {
          lotData[lot.id] = {
            nom: lot.nom,
            categorie: lot.categorie,
            nbProjets: 0,
            coutTotal: 0,
            surfaceTotal: 0,
            ratioM2: 0,
            coutTotalProjets: 0,
            ratioCoutLotSurProjet: 0
          };
        }
        
        lotData[lot.id].nbProjets++;
        lotData[lot.id].coutTotal += lot.cout;
        lotData[lot.id].surfaceTotal += lot.surface;
        lotData[lot.id].coutTotalProjets += projet.coutTotal;
      });
    });

    // Calculate ratios and total cost for normalization
    let totalCoutAllLots = 0;
    Object.values(lotData).forEach(lot => {
      lot.ratioM2 = lot.surfaceTotal > 0 ? Math.round(lot.coutTotal / lot.surfaceTotal) : 0;
      totalCoutAllLots += lot.coutTotal;
    });

    // Normalize percentages to sum to 100%
    Object.values(lotData).forEach(lot => {
      lot.ratioCoutLotSurProjet = totalCoutAllLots > 0 ? Math.round((lot.coutTotal / totalCoutAllLots) * 100 * 100) / 100 : 0;
    });

    return lotData;
  }, [filteredProjets]);

  // Group lots by category
  const lotsByCategory = useMemo(() => {
    const categories: { [categorieId: string]: any[] } = {};
    
    Object.values(lotRatios).forEach(lot => {
      if (!categories[lot.categorie]) {
        categories[lot.categorie] = [];
      }
      categories[lot.categorie].push(lot);
    });

    // Sort lots within each category by name
    Object.values(categories).forEach(categoryLots => {
      categoryLots.sort((a, b) => a.nom.localeCompare(b.nom));
    });

    return categories;
  }, [lotRatios]);

  // Get category name by ID
  const getCategoryName = (categorieId: string) => {
    const category = CATEGORIES_LOTS.find(cat => cat.id === categorieId);
    return category ? category.nom : categorieId;
  };

  // Démarrer un chiffrage : promeut les lots Ratio courants vers Chiffrage (1 lot = 1 onglet, budget pré-rempli)
  const lotRatioEntries = Object.entries(lotRatios) as Array<[string, any]>;
  const canPromote = lotRatioEntries.length > 0 && !!onNavigateToChiffrage;
  const handlePromoteToChiffrage = () => {
    if (!canPromote) return;
    const surface = parseFloat(surfaceInput) || 0;
    const equivalentM2 = surface * unitConversionFactor;
    const promotedLots = lotRatioEntries.map(([id, lot]) => ({
      id,
      nom: lot.nom as string,
      categorie: lot.categorie as string,
      budgetInitial: Math.round((lot.ratioM2 as number) * equivalentM2),
    }));
    setPendingPromotion({
      lots: promotedLots,
      surface,
      unitLabel: activeUnit.label,
    });
    onNavigateToChiffrage?.();
  };

  // Toggle project expansion
  const toggleProjet = (projetId: string) => {
    setExpandedProjets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projetId)) {
        newSet.delete(projetId);
      } else {
        newSet.add(projetId);
      }
      return newSet;
    });
  };

  // Toggle lot category expansion
  const toggleLotCategory = (categorieId: string) => {
    setExpandedLotCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categorieId)) {
        newSet.delete(categorieId);
      } else {
        newSet.add(categorieId);
      }
      return newSet;
    });
  };

  // Toggle lot expansion
  const toggleLot = (lotId: string) => {
    setExpandedLots(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lotId)) {
        newSet.delete(lotId);
      } else {
        newSet.add(lotId);
      }
      return newSet;
    });
  };



  // Utility functions
  const getRatioColor = (ratio: number) => {
    if (ratio < 1000) return 'text-green-600';
    if (ratio < 1500) return 'text-yellow-600';
    if (ratio < 2000) return 'text-orange-600';
    return 'text-red-600';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-violet-700 leading-tight">Ratios des projets</h1>
          <p className="mt-2 text-base text-gray-600">
            Analyser les ratios de vos projets antérieurs pour optimiser vos estimations
          </p>
        </header>

        {/* Unit selector + bouton de promotion vers Chiffrage */}
        <section className="mb-6 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <RatioUnitSelector
              units={AVAILABLE_UNITS.map(u => ({
                key: u.key,
                label: u.label,
                count: unitCounts[u.key] ?? 0,
              }))}
              activeKey={activeUnitKey}
              onChange={setActiveUnitKey}
              maxVisible={3}
            />
          </div>
          {onNavigateToChiffrage && (
            <button
              type="button"
              onClick={handlePromoteToChiffrage}
              disabled={!canPromote}
              title={
                canPromote
                  ? `Crée un chiffrage avec ${lotRatioEntries.length} lot${lotRatioEntries.length > 1 ? 's' : ''} pré-rempli${lotRatioEntries.length > 1 ? 's' : ''} (1 lot = 1 onglet, budget initial = ratio × quantité)`
                  : 'Aucun lot disponible avec les filtres courants'
              }
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold shadow-sm hover:bg-violet-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed disabled:shadow-none flex-shrink-0"
            >
              <ArrowRightCircleIcon className="w-5 h-5" />
              Démarrer un chiffrage
            </button>
          )}
        </section>

        {/* Filters Section */}
        <section className="mb-6">
          <ProjectSearchAndFilters
            placeholder="Rechercher un chantier..."
            dateOptions={Array.from(new Set(ratioData.map(p => p.dateCreation))).sort()}
          />
        </section>





        {/* Navigation Tabs */}
        <section className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('ratioOperation')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'ratioOperation'
                    ? 'border-violet-500 text-violet-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <BuildingOfficeIcon className="w-5 h-5" />
                  <span>Ratio par opération</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('ratioLot')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'ratioLot'
                    ? 'border-violet-500 text-violet-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <CalculatorIcon className="w-5 h-5" />
                  <span>Ratio par lot</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('maitriseBudget')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'maitriseBudget'
                    ? 'border-violet-500 text-violet-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <ShieldCheckIcon className="w-5 h-5" />
                  <span>Maîtrise budgétaire</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('auditLot')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'auditLot'
                    ? 'border-violet-500 text-violet-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <MagnifyingGlassIcon className="w-5 h-5" />
                  <span>Audit lot</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('facadeProto')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'facadeProto'
                    ? 'border-violet-500 text-violet-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <BeakerIcon className="w-5 h-5" />
                  <span>Proto Façade</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('goProto')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'goProto'
                    ? 'border-violet-500 text-violet-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <BeakerIcon className="w-5 h-5" />
                  <span>Proto Gros Œuvre</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('goDirection')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'goDirection'
                    ? 'border-violet-500 text-violet-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <BeakerIcon className="w-5 h-5" />
                  <span>Vue Direction - Gros Œuvre</span>
                </div>
              </button>

            </nav>
          </div>
        </section>



        {/* Project Content */}
        {activeTab === 'ratioOperation' && (
          <>
            {/* Results Summary Bar */}
            <section className="mb-6">
              <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-xl p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {/* Nombre de projets */}
                  <div className="text-center">
                    <div className="text-2xl font-bold text-violet-600">
                      {filteredProjets.length}
                    </div>
                    <div className="text-sm text-gray-600">Projet{filteredProjets.length > 1 ? 's' : ''}</div>
                  </div>

                  {/* Quantité totale selon l'unité active */}
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {filteredProjets.reduce((sum, projet) => sum + getUnitQuantity(projet, activeUnitKey), 0).toLocaleString('fr-FR')} {activeUnit.shortLabel}
                    </div>
                    <div className="text-sm text-gray-600">{activeUnit.totalLabel}</div>
                  </div>

                  {/* Coût total */}
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {filteredProjets.reduce((sum, projet) => sum + projet.coutTotal, 0).toLocaleString('fr-FR')} €
                    </div>
                    <div className="text-sm text-gray-600">Coût total</div>
                  </div>

                  {/* Ratio moyen dans l'unité active */}
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {(() => {
                        const totalCost = filteredProjets.reduce((s, p) => s + p.coutTotal, 0);
                        const totalUnit = filteredProjets.reduce((s, p) => s + getUnitQuantity(p, activeUnitKey), 0);
                        return totalUnit > 0 ? Math.round(totalCost / totalUnit).toLocaleString('fr-FR') : '0';
                      })()} {activeUnit.ratioSuffix}
                    </div>
                    <div className="text-sm text-gray-600">Ratio moyen pondéré</div>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-4">
            {filteredProjets.map((projet) => {
              const isExpanded = expandedProjets.has(projet.id);
              const projetQty = getUnitQuantity(projet, activeUnitKey);
              const projetRatio = projetQty > 0 ? Math.round(projet.coutTotal / projetQty) : 0;

              return (
                <div key={projet.id} className="bg-white shadow rounded-xl overflow-hidden">
                  {/* Project Summary Card */}
                  <button
                    onClick={() => toggleProjet(projet.id)}
                    className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        {/* Project Header */}
                        <div className="flex items-center space-x-3 mb-3">
                          <h3 className="text-lg font-semibold text-gray-900">{projet.projet}</h3>
                          <span className="px-2 py-1 text-xs bg-violet-100 text-violet-700 rounded-full">
                            {projet.typeProjet}
                          </span>
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                            {projet.statut}
                          </span>
                        </div>

                                                       {/* Project Parameters */}
                               <div className="flex flex-wrap gap-2 mb-3">
                                 <span className="flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                                   <TagIcon className="w-3 h-3 mr-1" />
                                   {projet.parameters.TypologieProgramme}
                                 </span>
                                 <span className="flex items-center px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                                   <TagIcon className="w-3 h-3 mr-1" />
                                   {projet.parameters.ModaliteProgramme}
                                 </span>
                                 <span className="flex items-center px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                                   <TagIcon className="w-3 h-3 mr-1" />
                                   {projet.parameters.DevolutonMarche}
                                 </span>
                                 <span className="flex items-center px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full">
                                   <TagIcon className="w-3 h-3 mr-1" />
                                   {projet.parameters.Gamme}
                                 </span>
                               </div>

                        {/* Project Details */}
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <MapPinIcon className="w-4 h-4 mr-1" />
                            {projetQty.toLocaleString('fr-FR')} {activeUnit.shortLabel}
                          </span>
                          <span className="flex items-center">
                            <CalendarIcon className="w-4 h-4 mr-1" />
                            {formatDate(projet.dateCreation)}
                          </span>
                          <span className="flex items-center">
                            <InformationCircleIcon className="w-4 h-4 mr-1" />
                            {projet.lots.length} lot{projet.lots.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>

                      {/* Project Metrics */}
                      <div className="flex items-center space-x-6">
                        <div className="text-right">
                          <div className={`text-xl font-bold ${getRatioColor(projet.ratioM2)}`}>
                            {projetRatio.toLocaleString('fr-FR')} {activeUnit.ratioSuffix}
                          </div>
                          <div className="text-sm text-gray-500">Ratio global</div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold text-gray-900">
                            {projet.coutTotal.toLocaleString('fr-FR')} €
                          </div>
                          <div className="text-sm text-gray-500">Coût total</div>
                        </div>
                        {isExpanded ? (
                          <ChevronUpIcon className="w-6 h-6 text-gray-400" />
                        ) : (
                          <ChevronDownIcon className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Expanded Project Details */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 bg-gray-50">
                      <div className="p-6">
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4">Détails du projet</h4>
                          
                          {/* Project Parameters Details */}
                          <div className="mb-6 p-4 bg-white rounded-lg border">
                            <h5 className="font-medium text-gray-900 mb-3">Paramètres du projet</h5>
                                                               <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                     <div className="text-center">
                                       <div className="text-sm font-medium text-gray-600">Typologie</div>
                                       <div className="text-lg font-semibold text-blue-600">{projet.parameters.TypologieProgramme}</div>
                                     </div>
                                     <div className="text-center">
                                       <div className="text-sm font-medium text-gray-600">Modalité</div>
                                       <div className="text-lg font-semibold text-green-600">{projet.parameters.ModaliteProgramme}</div>
                                     </div>
                                     <div className="text-center">
                                       <div className="text-sm font-medium text-gray-600">Dévolution</div>
                                       <div className="text-lg font-semibold text-purple-600">{projet.parameters.DevolutonMarche}</div>
                                       </div>
                                     <div className="text-center">
                                       <div className="text-sm font-medium text-gray-600">Gamme</div>
                                       <div className="text-lg font-semibold text-orange-600">{projet.parameters.Gamme}</div>
                                     </div>
                                   </div>
                          </div>
                          
                          {/* Project Statistics */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <div className="bg-white p-4 rounded-lg border">
                              <div className="text-2xl font-bold text-violet-600">{projetRatio.toLocaleString('fr-FR')} {activeUnit.ratioSuffix}</div>
                              <div className="text-sm text-gray-600">Ratio global</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg border">
                              <div className="text-2xl font-bold text-blue-600">{projet.coutTotal.toLocaleString('fr-FR')} €</div>
                              <div className="text-sm text-gray-600">Coût total</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg border">
                              <div className="text-2xl font-bold text-green-600">{projetQty.toLocaleString('fr-FR')} {activeUnit.shortLabel}</div>
                              <div className="text-sm text-gray-600">{activeUnit.totalLabel.replace(/s $/, '').replace(/s$/, '')}</div>
                            </div>
                            <div className="bg-white p-4 rounded-lg border">
                              <div className="text-2xl font-bold text-orange-600">{projet.lots.length}</div>
                              <div className="text-sm text-gray-600">Nombre de lots</div>
                            </div>
                          </div>

                          {/* Lots Breakdown */}
                          <div>
                            <h5 className="font-medium text-gray-900 mb-3">Répartition par lots</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {projet.lots.map((lot) => (
                                <div key={lot.id} className="bg-white p-4 rounded-lg border">
                                  <div className="flex items-center justify-between mb-2">
                                    <h6 className="font-medium text-gray-900">{lot.nom}</h6>
                                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                                      {lot.categorie}
                                    </span>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-sm text-gray-600">Ratio</span>
                                      <span className={`font-semibold ${getRatioColor(lot.ratioM2)}`}>
                                        {Math.round(lot.ratioM2 * unitConversionFactor).toLocaleString('fr-FR')} {activeUnit.ratioSuffix}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-sm text-gray-600">Coût</span>
                                      <span className="font-semibold text-gray-900">
                                        {lot.cout.toLocaleString('fr-FR')} €
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-sm text-gray-600">% du projet</span>
                                      <span className="font-semibold text-blue-600">
                                        {lot.pourcentageProjet}%
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        </>
        )}


        {/* Ratio par lot Tab */}
        {activeTab === 'ratioLot' && (
          <>
            {/* Estimateur avec intervalles de confiance */}
            <section className="mb-6">
              <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-xl p-6">

                {/* Filtres locaux du calculateur */}
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-violet-700">Estimateur budgétaire par {activeUnit.label.toLowerCase()}</h3>
                    <span className="text-sm text-gray-500 bg-white border border-violet-200 px-3 py-1 rounded-full">
                      {filteredProjets.length} projet{filteredProjets.length !== 1 ? 's' : ''} comparables
                    </span>
                  </div>
                  {filteredProjets.length < 3 && (
                    <p className="mt-2 text-xs text-orange-600">
                      ⚠ Moins de 3 projets comparables — élargissez les filtres de la barre de recherche.
                    </p>
                  )}
                </div>

                {/* Surface + résumé budgétaire */}
                <div className="flex flex-wrap items-center gap-6 mb-4">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">{activeUnit.inputLabel} :</label>
                    <input
                      type="number"
                      value={surfaceInput}
                      onChange={(e) => setSurfaceInput(e.target.value)}
                      placeholder="Ex: 1000"
                      className="w-28 px-3 py-2 border border-violet-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm"
                    />
                    <span className="text-sm text-gray-500">{activeUnit.inputUnit}</span>
                  </div>
                  {totalEstimatedCostRange.median > 0 && (
                    <div className="flex gap-6">
                      <div className="text-center">
                        <div className="text-sm font-semibold text-gray-500">{totalEstimatedCostRange.min.toLocaleString('fr-FR')} €</div>
                        <div className="text-xs text-gray-400">Budget min</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-violet-700">{totalEstimatedCostRange.median.toLocaleString('fr-FR')} €</div>
                        <div className="text-xs text-violet-500">Budget médian</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-semibold text-gray-500">{totalEstimatedCostRange.max.toLocaleString('fr-FR')} €</div>
                        <div className="text-xs text-gray-400">Budget max</div>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => setShowCalculator(!showCalculator)}
                    className={`ml-auto px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 ${
                      showCalculator
                        ? 'bg-violet-600 text-white hover:bg-violet-700 shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                    }`}
                  >
                    <CalculatorIcon className="w-4 h-4" />
                    <span>{showCalculator ? 'Masquer le détail' : 'Détail par lot'}</span>
                  </button>
                </div>

                {/* Détail par lot — toggleable */}
                {showCalculator && (
                  <>
                    <div className="flex justify-center mb-5">
                      <div className="bg-white rounded-lg border border-violet-200 p-1">
                        <button
                          onClick={() => setCalculatorView('families')}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            calculatorView === 'families'
                              ? 'bg-violet-600 text-white shadow-md'
                              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                        >
                          Par famille de lot
                        </button>
                        <button
                          onClick={() => setCalculatorView('lots')}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            calculatorView === 'lots'
                              ? 'bg-violet-600 text-white shadow-md'
                              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                        >
                          Par lot
                        </button>
                      </div>
                    </div>

                    {/* Vue par famille de lot */}
                    {calculatorView === 'families' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {CATEGORIES_LOTS.map(categorie => {
                          const categoryLotStats = Array.from(lotsStats.values()).filter(d => d.categorie === categorie.id);
                          if (categoryLotStats.length === 0) return null;
                          const userInput = parseFloat(surfaceInput) || 0;
                          const catMin = categoryLotStats.reduce((s, d) => s + d.min, 0) * unitConversionFactor;
                          const catMedian = categoryLotStats.reduce((s, d) => s + d.median, 0) * unitConversionFactor;
                          const catMax = categoryLotStats.reduce((s, d) => s + d.max, 0) * unitConversionFactor;
                          const minNbProjets = Math.min(...categoryLotStats.map(d => d.nbProjets));
                          const span = catMax - catMin;
                          const medPct = span > 0 ? ((catMedian - catMin) / span) * 100 : 50;

                          return (
                            <div key={categorie.id} className="bg-white rounded-lg border border-violet-200 p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-gray-900 text-sm">{categorie.nom}</h4>
                                <span className="px-2 py-1 text-xs bg-violet-100 text-violet-700 rounded-full">
                                  {categoryLotStats.length} lot{categoryLotStats.length > 1 ? 's' : ''} · {minNbProjets} proj.
                                </span>
                              </div>
                              <div className="flex justify-between text-sm mb-2">
                                <span className="text-gray-500">Ratio médian :</span>
                                <span className="font-semibold text-violet-700">{Math.round(catMedian).toLocaleString('fr-FR')} {activeUnit.ratioSuffix}</span>
                              </div>
                              {/* Range bar simplifiée pour la catégorie */}
                              <div className="relative h-2 bg-gray-200 rounded-full mb-1.5">
                                <div
                                  className="absolute top-0 h-2 bg-violet-300 rounded-full"
                                  style={{ left: '0%', width: `${Math.min(medPct, 98)}%` }}
                                />
                                <div
                                  className="absolute w-0.5 h-4 -top-1 bg-violet-700 rounded"
                                  style={{ left: `${medPct}%`, transform: 'translateX(-50%)' }}
                                />
                              </div>
                              <div className="flex justify-between text-xs text-gray-400 mb-3">
                                <span>{Math.round(catMin).toLocaleString('fr-FR')}</span>
                                <span>{Math.round(catMax).toLocaleString('fr-FR')} {activeUnit.ratioSuffix}</span>
                              </div>
                              {userInput > 0 && (
                                <div className="pt-2 border-t border-gray-100 flex justify-between text-xs">
                                  <span className="text-gray-400">{Math.round(catMin * userInput).toLocaleString('fr-FR')} €</span>
                                  <span className="font-semibold text-violet-700">{Math.round(catMedian * userInput).toLocaleString('fr-FR')} €</span>
                                  <span className="text-gray-400">{Math.round(catMax * userInput).toLocaleString('fr-FR')} €</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Vue par lot individuel avec range bars */}
                    {calculatorView === 'lots' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Array.from(lotsStats.entries()).map(([lotName, dist]) => {
                          const scaledDist = {
                            ...dist,
                            min: Math.round(dist.min * unitConversionFactor),
                            p25: Math.round(dist.p25 * unitConversionFactor),
                            median: Math.round(dist.median * unitConversionFactor),
                            p75: Math.round(dist.p75 * unitConversionFactor),
                            p90: Math.round(dist.p90 * unitConversionFactor),
                            max: Math.round(dist.max * unitConversionFactor),
                            mean: Math.round(dist.mean * unitConversionFactor),
                          };
                          return (
                            <div key={lotName} className="bg-white rounded-lg border border-violet-200 p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-medium text-gray-900 text-sm">{lotName}</h4>
                                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                                  {getCategoryName(dist.categorie)}
                                </span>
                              </div>
                              <RatioRangeBar dist={scaledDist} surface={parseFloat(surfaceInput) || 0} ratioSuffix={activeUnit.ratioSuffix} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>

            {/* Lots by Category */}
            <section className="space-y-6">
              {Object.entries(lotsByCategory).map(([categorieId, lots]) => {
                const categoryName = getCategoryName(categorieId);
                
                // Calculate category totals
                const totalCout = lots.reduce((sum, lot) => sum + lot.coutTotal, 0);
                const totalSurface = lots.reduce((sum, lot) => sum + lot.surfaceTotal, 0);
                const totalQuantity = unitConversionFactor > 0 ? Math.round(totalSurface / unitConversionFactor) : 0;
                const ratioMoyen = totalQuantity > 0 ? Math.round(totalCout / totalQuantity) : 0;
                
                // Calculate percentage based on total cost of all categories (normalized)
                const totalCoutAllCategories = Object.values(lotsByCategory).reduce((sum, catLots) => 
                  sum + catLots.reduce((catSum, lot) => catSum + lot.coutTotal, 0), 0
                );
                const ratioCoutSurProjet = totalCoutAllCategories > 0 ? Math.round((totalCout / totalCoutAllCategories) * 100 * 100) / 100 : 0;

                return (
                  <div key={categorieId} className="bg-white shadow rounded-xl overflow-hidden">
                    {/* Category Header - Clickable for toggle */}
                    <button
                      onClick={() => toggleLotCategory(categorieId)}
                      className="w-full bg-white border-b border-gray-200 p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center justify-between flex-1">
                          <div className="min-w-[200px]">
                            <h3 className="text-lg font-semibold text-gray-900">{categoryName}</h3>
                            <p className="text-sm text-gray-600">
                              {lots.length} lot{lots.length > 1 ? 's' : ''}
                            </p>
                          </div>
                          
                          {/* Nombre de projets */}
                          <div className="text-center min-w-[120px]">
                            <div className="text-base font-semibold text-gray-700">
                              {Math.max(...lots.map(lot => lot.nbProjets))}
                            </div>
                            <div className="text-xs text-gray-600">Projets</div>
                          </div>
                          
                          {/* Quantité totale */}
                          <div className="text-center min-w-[140px]">
                            <div className="text-base font-semibold text-gray-700">{totalQuantity.toLocaleString('fr-FR')} {activeUnit.shortLabel}</div>
                            <div className="text-xs text-gray-600">Quantité</div>
                          </div>

                          {/* Coût total */}
                          <div className="text-center min-w-[160px]">
                            <div className="text-base font-semibold text-gray-700">{totalCout.toLocaleString('fr-FR')} €</div>
                            <div className="text-xs text-gray-600">Coût</div>
                          </div>

                          {/* Ratio moyen */}
                          <div className="text-center min-w-[140px]">
                            <div className="text-lg font-bold text-blue-600">{ratioMoyen.toLocaleString('fr-FR')} {activeUnit.ratioSuffix}</div>
                            <div className="text-xs text-blue-600">Ratio</div>
                          </div>
                          
                          {/* % sur coût projet */}
                          <div className="text-center min-w-[140px]">
                            <div className="text-lg font-bold text-green-600">{ratioCoutSurProjet}%</div>
                            <div className="text-xs text-green-600">% Projet</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center ml-4">
                          {expandedLotCategories.has(categorieId) ? (
                            <ChevronUpIcon className="w-5 h-5 text-blue-400" />
                          ) : (
                            <ChevronDownIcon className="w-5 h-5 text-blue-400" />
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Lots in this Category - Expandable */}
                    {expandedLotCategories.has(categorieId) && (
                      <div className="border-t border-blue-200 bg-gray-50">
                        <div className="p-6">
                          <div className="space-y-4">
                            {lots.map((lot) => (
                              <div key={lot.nom} className="bg-white rounded-lg border p-4">
                                {/* Lot Header */}
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => toggleLot(lot.nom)}
                                    className="w-full text-left hover:bg-gray-50 p-2 rounded transition-colors"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center justify-between flex-1">
                                        <div className="min-w-[250px]">
                                          <h4 className="font-medium text-gray-900">{lot.nom}</h4>
                                        </div>

                                        {/* Nombre de projets */}
                                        <div className="text-center min-w-[100px]">
                                          <div className="text-sm font-semibold text-gray-700">{lot.nbProjets}</div>
                                          <div className="text-xs text-gray-600">Projets</div>
                                        </div>

                                        {/* Quantité totale */}
                                        <div className="text-center min-w-[120px]">
                                          <div className="text-sm font-semibold text-gray-700">{Math.round(lot.surfaceTotal / (unitConversionFactor || 1)).toLocaleString('fr-FR')} {activeUnit.shortLabel}</div>
                                          <div className="text-xs text-gray-600">Quantité</div>
                                        </div>

                                        {/* Coût total */}
                                        <div className="text-center min-w-[140px]">
                                          <div className="text-sm font-semibold text-gray-700">{lot.coutTotal.toLocaleString('fr-FR')} €</div>
                                          <div className="text-xs text-gray-600">Coût</div>
                                        </div>

                                        {/* Ratio dans l'unité active */}
                                        <div className="text-center min-w-[120px]">
                                          <div className="text-sm font-bold text-blue-600">{Math.round(lot.ratioM2 * unitConversionFactor).toLocaleString('fr-FR')} {activeUnit.ratioSuffix}</div>
                                          <div className="text-xs text-blue-600">Ratio</div>
                                        </div>

                                        {/* % sur coût projet */}
                                        <div className="text-center min-w-[120px]">
                                          <div className="text-sm font-bold text-green-600">{lot.ratioCoutLotSurProjet}%</div>
                                          <div className="text-xs text-green-600">% Projet</div>
                                        </div>
                                      </div>

                                      <div className="flex items-center ml-4">
                                        {expandedLots.has(lot.nom) ? (
                                          <ChevronUpIcon className="w-4 h-4 text-blue-400" />
                                        ) : (
                                          <ChevronDownIcon className="w-4 h-4 text-blue-400" />
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                </div>

                                {/* Lot Details - Expandable */}
                                {expandedLots.has(lot.nom) && (
                                  <div className="mt-4 pt-4 border-t border-gray-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                      {/* Projets utilisant ce lot */}
                                      <div className="bg-gray-50 p-3 rounded-lg">
                                        <h6 className="font-medium text-gray-900 mb-2">Projets utilisant ce lot</h6>
                                        <div className="space-y-2">
                                          {filteredProjets
                                            .filter(projet => projet.lots.some(l => l.nom === lot.nom))
                                            .map(projet => (
                                              <div key={projet.id} className="text-sm">
                                                <div className="font-medium text-gray-900">{projet.projet}</div>
                                                <div className="text-gray-600">
                                                  {projet.lots.find(l => l.nom === lot.nom)?.cout.toLocaleString('fr-FR')} € 
                                                  ({projet.lots.find(l => l.nom === lot.nom)?.pourcentageProjet}%)
                                                </div>
                                              </div>
                                            ))}
                                        </div>
                                      </div>
                                      
                                      {/* Statistiques du lot */}
                                      <div className="bg-gray-50 p-3 rounded-lg">
                                        <h6 className="font-medium text-gray-900 mb-2">Statistiques</h6>
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Coût moyen par projet:</span>
                                            <span className="font-medium text-gray-900">
                                              {Math.round(lot.coutTotal / lot.nbProjets).toLocaleString('fr-FR')} €
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Quantité moyenne:</span>
                                            <span className="font-medium text-gray-900">
                                              {Math.round((lot.surfaceTotal / lot.nbProjets) / (unitConversionFactor || 1)).toLocaleString('fr-FR')} {activeUnit.shortLabel}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Répartition par projet */}
                                      <div className="bg-gray-50 p-3 rounded-lg">
                                        <h6 className="font-medium text-gray-900 mb-2">Répartition</h6>
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Poids dans les projets:</span>
                                            <span className="font-medium text-green-600">
                                              {lot.ratioCoutLotSurProjet}%
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Ratio stable:</span>
                                            <span className="font-medium text-blue-600">
                                              {Math.round(lot.ratioM2 * unitConversionFactor).toLocaleString('fr-FR')} {activeUnit.ratioSuffix}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          </>
        )}



        {/* Maîtrise budgétaire Tab */}
        {activeTab === 'maitriseBudget' && (
          <MaitriseBudget allProjects={filteredProjets} />
        )}

        {/* Audit lot Tab */}
        {activeTab === 'auditLot' && (
          <AuditLot allProjects={filteredProjets} />
        )}

        {/* Proto Façade Tab */}
        {activeTab === 'facadeProto' && (
          <RatioFacadePrototype />
        )}

        {/* Proto Gros Œuvre Tab */}
        {activeTab === 'goProto' && (
          <RatioGrosOeuvrePrototype />
        )}

        {/* Vue Direction Gros Œuvre Tab */}
        {activeTab === 'goDirection' && (
          <RatioGrosOeuvreDirection />
        )}

        {/* No Results Message */}
        {filteredProjets.length === 0 && activeTab !== 'facadeProto' && activeTab !== 'goProto' && activeTab !== 'goDirection' && (
          <section className="p-8 bg-white shadow rounded-xl text-center">
            <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun projet trouvé</h3>
            <p className="text-gray-600">Ajustez vos filtres pour voir les résultats</p>
          </section>
        )}
      </div>
    </div>
  );
};

export default Ratio; 
